import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import {
  Wallet,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownLeft,
  Search,
  Eye,
  Upload,
  CheckCircle,
  Clock,
  XCircle,
  Loader
} from 'lucide-react';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import {
  useGetUserWalletBalanceQuery,
  useGetUserWalletTransactionsQuery,
  useGetUserWalletStatsQuery,
  useAddFundsToWalletMutation,
  useWithdrawFundsFromWalletMutation,
  useGetActivePaymentMethodsQuery,
} from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const UserWallet: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const [showAddFundsModal, setShowAddFundsModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTransactionDetail, setShowTransactionDetail] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<'all' | 'deposit' | 'withdrawal' | 'investment' | 'sponsorship' | 'booking' | 'marketplace'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Add Funds Form State
  const [addFundsForm, setAddFundsForm] = useState({
    amount: '',
    payment_method: '',
    transaction_reference: '',
    description: '',
    payment_screenshot: null as File | null,
  });

  // Withdraw Form State
  const [withdrawForm, setWithdrawForm] = useState({
    amount: '',
    description: '',
  });

  usePageMetadata('wallet');

  // API Queries
  const { data: balanceData, isLoading: isLoadingBalance } = useGetUserWalletBalanceQuery(undefined, {
    skip: !user,
  });

  const { data: statsData, isLoading: isLoadingStats } = useGetUserWalletStatsQuery(undefined, {
    skip: !user,
  });

  const { data: transactionsData, isLoading: isLoadingTransactions } = useGetUserWalletTransactionsQuery({
    page: currentPage,
    limit: 10,
    transaction_type: transactionTypeFilter,
  }, {
    skip: !user,
  });

  const { data: paymentMethodsData } = useGetActivePaymentMethodsQuery(undefined, {
    skip: !user || !showAddFundsModal,
  });

  // Mutations
  const [addFunds, { isLoading: isAddingFunds }] = useAddFundsToWalletMutation();
  const [withdrawFunds, { isLoading: isWithdrawing }] = useWithdrawFundsFromWalletMutation();

  const walletBalance = balanceData?.data?.wallet_balance || 0;
  const currencySymbol = balanceData?.data?.currency_symbol || '$';
  const stats = statsData?.data;
  const transactions = transactionsData?.data?.transactions || [];
  const pagination = transactionsData?.data?.pagination;
  const paymentMethods = paymentMethodsData?.data || [];

  const formatCurrency = (amount: number) => {
    return `${currencySymbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString: string | Date | null | undefined) => formatDateTimeUtil(dateString);;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="success" className="bg-green-500 text-white"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge variant="warning" className="bg-yellow-500 text-white"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'rejected':
        return <Badge variant="danger" className="bg-red-500 text-white"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'failed':
        return <Badge variant="danger" className="bg-red-500 text-white"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTransactionTypeIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="w-4 h-4 text-green-600" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-4 h-4 text-red-600" />;
      default:
        return <DollarSign className="w-4 h-4 text-gray-600" />;
    }
  };

  const handleAddFunds = async () => {
    if (!addFundsForm.amount || !addFundsForm.payment_method || !addFundsForm.payment_screenshot) {
      toast.error('Please fill all required fields');
      return;
    }

    const amount = parseFloat(addFundsForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await addFunds({
        amount,
        payment_method: addFundsForm.payment_method,
        transaction_reference: addFundsForm.transaction_reference || undefined,
        description: addFundsForm.description || undefined,
        payment_screenshot: addFundsForm.payment_screenshot,
      }).unwrap();
      toast.success('Funds request submitted successfully! Pending admin verification.');
      setShowAddFundsModal(false);
      setAddFundsForm({
        amount: '',
        payment_method: '',
        transaction_reference: '',
        description: '',
        payment_screenshot: null,
      });
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to add funds');
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawForm.amount) {
      toast.error('Please enter an amount');
      return;
    }

    const amount = parseFloat(withdrawForm.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount > walletBalance) {
      toast.error('Insufficient wallet balance');
      return;
    }

    try {
      await withdrawFunds({
        amount,
        description: withdrawForm.description || undefined,
      }).unwrap();
      toast.success('Withdrawal request submitted successfully! Pending admin approval.');
      setShowWithdrawModal(false);
      setWithdrawForm({
        amount: '',
        description: '',
      });
    } catch (error: any) {
      toast.error(error?.data?.message || 'Failed to withdraw funds');
    }
  };

  const filteredTransactions = transactions.filter((transaction) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      transaction.description?.toLowerCase().includes(searchLower) ||
      transaction.transaction_reference?.toLowerCase().includes(searchLower) ||
      transaction.payment_method?.toLowerCase().includes(searchLower)
    );
  });

  if (!user) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <Card className="p-12 text-center">
              <p className="text-gray-600 mb-4">Please login to view your wallet.</p>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">My Wallet</h1>
            <p className="text-gray-600">Manage your wallet balance and transactions</p>
          </div>

          {/* Wallet Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-blue-100 text-sm mb-1">Total Balance</p>
                  {isLoadingBalance ? (
                    <Loader className="w-6 h-6 animate-spin" />
                  ) : (
                    <p className="text-3xl font-bold">{formatCurrency(walletBalance)}</p>
                  )}
                </div>
                <Wallet className="w-12 h-12 text-blue-200" />
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Deposits</p>
                  {isLoadingStats ? (
                    <Loader className="w-6 h-6 animate-spin" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.total_deposits || 0)}</p>
                  )}
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm mb-1">Total Withdrawals</p>
                  {isLoadingStats ? (
                    <Loader className="w-6 h-6 animate-spin" />
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats?.total_withdrawals || 0)}</p>
                  )}
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <TrendingDown className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </Card>
          </div>

          {/* Transactions Section */}
          <Card className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Transactions</h2>
            </div>

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search transactions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={transactionTypeFilter}
                onChange={(e) => {
                  setTransactionTypeFilter(e.target.value as any);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="investment">Investment</option>
                <option value="sponsorship">Sponsorship</option>
                <option value="booking">Booking</option>
                <option value="marketplace">Marketplace</option>
              </select>
            </div>

            {/* Transactions List */}
            {isLoadingTransactions ? (
              <div className="flex items-center justify-center py-12">
                <Loader className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="text-center py-12">
                <Wallet className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Transactions Yet</h3>
                <p className="text-gray-600">Your transaction history will appear here</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {filteredTransactions.map((transaction) => (
                    <div
                      key={transaction.transaction_id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setShowTransactionDetail(transaction)}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          {getTransactionTypeIcon(transaction.transaction_type)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900 capitalize">{transaction.transaction_type}</p>
                            {getStatusBadge(transaction.status)}
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{formatDate(transaction.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold text-lg ${transaction.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'
                          }`}>
                          {transaction.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(transaction.amount)}
                        </p>
                        <p className="text-xs text-gray-500">Balance: {formatCurrency(transaction.balance_after)}</p>
                      </div>
                      <Eye className="w-5 h-5 text-gray-400 ml-4" />
                    </div>
                  ))}
                </div>

                {/* Pagination */}
                {pagination && pagination.total_pages > 1 && (
                  <div className="mt-6 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {pagination.current_page} of {pagination.total_pages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
                      disabled={currentPage === pagination.total_pages}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>

      {/* Add Funds Modal */}
      {showAddFundsModal && (
        <Modal
          isOpen={showAddFundsModal}
          onClose={() => {
            setShowAddFundsModal(false);
            setAddFundsForm({
              amount: '',
              payment_method: '',
              transaction_reference: '',
              description: '',
              payment_screenshot: null,
            });
          }}
          title="Add Funds to Wallet"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
              <Input
                type="number"
                placeholder="Enter amount"
                value={addFundsForm.amount}
                onChange={(e) => setAddFundsForm({ ...addFundsForm, amount: e.target.value })}
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
              <select
                value={addFundsForm.payment_method}
                onChange={(e) => setAddFundsForm({ ...addFundsForm, payment_method: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              >
                <option value="">Select payment method</option>
                {paymentMethods.map((method) => (
                  <option key={method.payment_gateway_options_id} value={method.payment_method_name}>
                    {method.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Reference</label>
              <Input
                type="text"
                placeholder="Optional transaction reference"
                value={addFundsForm.transaction_reference}
                onChange={(e) => setAddFundsForm({ ...addFundsForm, transaction_reference: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                placeholder="Optional description"
                value={addFundsForm.description}
                onChange={(e) => setAddFundsForm({ ...addFundsForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Screenshot *</label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  <span className="text-sm">Upload Screenshot</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setAddFundsForm({ ...addFundsForm, payment_screenshot: file });
                      }
                    }}
                  />
                </label>
                {addFundsForm.payment_screenshot && (
                  <span className="text-sm text-gray-600">{addFundsForm.payment_screenshot.name}</span>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleAddFunds}
                disabled={isAddingFunds}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isAddingFunds ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Request
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowAddFundsModal(false);
                  setAddFundsForm({
                    amount: '',
                    payment_method: '',
                    transaction_reference: '',
                    description: '',
                    payment_screenshot: null,
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Withdraw Modal */}
      {showWithdrawModal && (
        <Modal
          isOpen={showWithdrawModal}
          onClose={() => {
            setShowWithdrawModal(false);
            setWithdrawForm({ amount: '', description: '' });
          }}
          title="Withdraw Funds"
        >
          <div className="space-y-4">
            <div className="p-4 bg-white border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-800">
                <strong>Available Balance:</strong> {formatCurrency(walletBalance)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Amount *</label>
              <Input
                type="number"
                placeholder="Enter amount to withdraw"
                value={withdrawForm.amount}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                min="0"
                step="0.01"
                max={walletBalance.toString()}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                placeholder="Optional reason for withdrawal"
                value={withdrawForm.description}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                rows={3}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleWithdraw}
                disabled={isWithdrawing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {isWithdrawing ? <Loader className="w-4 h-4 animate-spin mr-2" /> : null}
                Submit Withdrawal Request
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setShowWithdrawModal(false);
                  setWithdrawForm({ amount: '', description: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Transaction Detail Modal */}
      {showTransactionDetail && (
        <Modal
          isOpen={!!showTransactionDetail}
          onClose={() => setShowTransactionDetail(null)}
          title="Transaction Details"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Transaction ID</p>
                <p className="font-semibold">#{showTransactionDetail.transaction_id}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Status</p>
                {getStatusBadge(showTransactionDetail.status)}
              </div>
              <div>
                <p className="text-sm text-gray-600">Type</p>
                <p className="font-semibold capitalize">{showTransactionDetail.transaction_type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount</p>
                <p className={`font-bold text-lg ${showTransactionDetail.transaction_type === 'deposit' ? 'text-green-600' : 'text-red-600'
                  }`}>
                  {showTransactionDetail.transaction_type === 'deposit' ? '+' : '-'}
                  {formatCurrency(showTransactionDetail.amount)}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Balance Before</p>
                <p className="font-semibold">{formatCurrency(showTransactionDetail.balance_before)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Balance After</p>
                <p className="font-semibold">{formatCurrency(showTransactionDetail.balance_after)}</p>
              </div>
              {showTransactionDetail.payment_method && (
                <div>
                  <p className="text-sm text-gray-600">Payment Method</p>
                  <p className="font-semibold">{showTransactionDetail.payment_method}</p>
                </div>
              )}
              {showTransactionDetail.transaction_reference && (
                <div>
                  <p className="text-sm text-gray-600">Reference</p>
                  <p className="font-semibold">{showTransactionDetail.transaction_reference}</p>
                </div>
              )}
            </div>

            <div>
              <p className="text-sm text-gray-600 mb-1">Description</p>
              <p className="font-semibold">{showTransactionDetail.description}</p>
            </div>

            {showTransactionDetail.payment_screenshot && (
              <div>
                <p className="text-sm text-gray-600 mb-2">Payment Screenshot</p>
                <img
                  src={showTransactionDetail.payment_screenshot}
                  alt="Payment screenshot"
                  className="w-full rounded-lg border border-gray-200"
                />
              </div>
            )}

            {showTransactionDetail.rejection_reason && (
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-red-800">Rejection Reason</p>
                <p className="text-sm text-red-600">{showTransactionDetail.rejection_reason}</p>
              </div>
            )}

            {showTransactionDetail.verified_by && (
              <div>
                <p className="text-sm text-gray-600">Verified By</p>
                <p className="font-semibold">{showTransactionDetail.verified_by}</p>
                {showTransactionDetail.verified_at && (
                  <p className="text-xs text-gray-500">{formatDate(showTransactionDetail.verified_at)}</p>
                )}
              </div>
            )}

            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">Date</p>
              <p className="font-semibold">{formatDate(showTransactionDetail.created_at)}</p>
            </div>

            <Button onClick={() => setShowTransactionDetail(null)} className="w-full">
              Close
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};

export default UserWallet;
